import { Injectable } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, interval, Subject, Subscription, switchMap, timer } from 'rxjs';
import { TableColumn } from 'src/app/shared/models/CommandCenterUI/TableDataModel.model';
import { AppMessageSeverity } from 'src/app/shared/models/enums/AppMessageSeverity.enum';
import { UserRoleEnum } from 'src/app/shared/models/enums/user-role.enum';
import { User, UserTableColumnPreference, UserVenue } from 'src/app/shared/models/user.model';
import { AppService } from './app.service';
import { DealerService } from './dealer.service';
import { FocuslinkService } from './focuslink.service';
import { LocalDataStoreService } from './local-data-store.service';
import { NavigationService } from './navigation.service';
import { OOVenueService } from './venue-service';

@Injectable({
	providedIn: 'root',
})
export class UserService {
	user: User;
	userVenues: BehaviorSubject<UserVenue[]> = new BehaviorSubject<UserVenue[]>([]);

	sessionAge: number;
	sessionAgeDuration: Subject<number>;

	userAdvertisement: BehaviorSubject<boolean>;

	userSubscription: BehaviorSubject<User> = new BehaviorSubject<User>(new User());
	userSessionExpirationNotification: Subscription;
	userSessionSubscription: Subscription;
	userToken: string;

	constructor(
		private focuslink: FocuslinkService,
		public localStorage: LocalDataStoreService,
		private navigationService: NavigationService,
		public venueService: OOVenueService,
		private dealerService: DealerService,
		public app: AppService
	) {
		if (!this.user) {
			this.handleNullUser();
		}
		this.sessionAgeDuration = new Subject<number>();
		this.userAdvertisement = new BehaviorSubject<boolean>(false);

		this.userSessionSubscription = timer(0, 10000)
			.pipe(switchMap(() => this.monitorSession()))
			.subscribe();
	}

	async handleNullUser() {
		const user: User = await this.localStorage.get('user');
		if (user) {
			this.user = user;
			this.userSubscription.next(this.user);
			this.sessionAge = user.sessionStart;
		}
	}

	async initUser(user: User, token: string) {
		const sessionStartTime: number = moment().valueOf();
		this.user = user;
		this.userSubscription.next(user);
		this.userToken = token;

		this.user.sessionStart = sessionStartTime;
		this.sessionAge = sessionStartTime;

		await this.localStorage.set('user', this.user);
		await this.localStorage.set('utoken', token);
		if (moment().isBefore(moment('2021-11-16 00:30', 'YYYY-MM-DD HH:mm').utc())) {
			this.userAdvertisement.next(true);
		}
		this.loadVenuesFromUserVenue();
		this.userSubscription.next(this.user);

		switch (this.user.role) {
			case UserRoleEnum.GOD: {
				console.log('Welcome your Holiness');
				this.dealerService.initSuperUser();
				break;
			}
			case UserRoleEnum.SUPERADMIN: {
				console.log('Super admin');

				this.dealerService.initSuperUser();
				break;
			}
			case UserRoleEnum.DEALERADMIN: {
				this.dealerService.initDealer(this.user.dealerKey);
			}
			case UserRoleEnum.DEALERUSER: {
				this.dealerService.initDealer(this.user.dealerKey);
			}
			default: {
			}
		}

		this.venueService.userOrderNotificationTone = this.user.commandCenterOrderNotificationTone;
	}

	loadVenuesFromUserVenue() {
		this.user.UserVenue.forEach((uv) => {
			!uv.venueName && (uv.venueName = 'Unnamed');
		});
		this.userVenues.next(this.user.UserVenue.sort((a, b) => (a.venueName > b.venueName ? 1 : -1)));
	}

	async logout(source?: string) {
		if (!source || source !== 'inactive') {
			await this.localStorage.remove('user');
			await this.localStorage.remove('utoken');
			await this.localStorage.remove('venues');
			await this.localStorage.remove('venue');
			this.sessionAge = undefined;
			this.userVenues.next([]);
			this.userSubscription.next(undefined);
			this.venueService.userOrderNotificationTone = undefined;
			this.user = undefined;
		}
	}

	async menuItemToggleUserFavorite(key: string): Promise<any> {
		if (!this.user.preferences.menusFavorite?.some((i) => i === key)) {
			//remove item, as it already exists
			if (!this.user.preferences.menusFavorite) this.user.preferences.menusFavorite = [];
			this.user.preferences.menusFavorite.push(key);
		} else {
			this.user.preferences.menusFavorite.splice(
				this.user.preferences.menusFavorite.findIndex((i) => i === key),
				1
			);
		}
		await this.savePreference();
		this.localStorage.set('user', this.user);
		this.userSubscription.next(this.user);
		return;
	}

	async monitorSession() {
		if (this.user) {
			const sessionAgeDuration = moment().diff(moment(this.sessionAge), 'seconds');
			this.sessionAgeDuration.next(this.navigationService.lastPageVisitDateTime);
			if (sessionAgeDuration > 240) {
				await this.refreshUserToken(this.user.key, this.userToken);
			}

			this.userSessionExpirationNotification = interval(60000)
				.pipe(switchMap(() => this.logout('inactive')))
				.subscribe();

			return sessionAgeDuration;
		}
	}

	async refreshUserToken(userKey: string, oldToken: string) {
		const resp = await this.focuslink.userRefresh(userKey);
		this.userToken = resp.token;
		this.sessionAge = moment().valueOf();

		this.localStorage.set('utoken', this.userToken);

		return this.userToken;
	}

	async sessionAgeObservable() {
		return this.sessionAgeDuration.asObservable();
	}

	userSessionStateSubscriber() {
		this.userSubscription.subscribe((user) => {
			if (user.commandCenterOrderNotificationTone !== this.venueService.userOrderNotificationTone) {
				this.venueService.userOrderNotificationTone;
			}
		});
	}

	async userDashboardDefaultSave(dashboard: string) {
		this.userSubscription.value.dashboardDefault = dashboard;
		await this.focuslink.userEdit(
			new User({
				key: this.userSubscription.value.key,
				dashboardDefault: dashboard,
			})
		);
		this.userSubscription.next(this.userSubscription.value);
		this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: '', detail: `Dashboard favorite updated.` });
		return this.userSubscription.value;
	}

	async userMenuDefaultSave(menu: string) {
		this.userSubscription.value.preferences.menuDefault = menu;
		await this.focuslink.userEdit(this.userSubscription.value);
		this.userSubscription.next(this.userSubscription.value);
		this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: '', detail: `Homepage updated.` });
		return this.userSubscription.value;
	}

	async setColumnPreferences(tableName: string, tableColumnModel: TableColumn[]) {
		const existing = this.userSubscription.value.preferences.tableColumnPreference.find((i) => i.tableId === tableName);
		if (!existing) {
			this.userSubscription.value.preferences.tableColumnPreference.push(
				new UserTableColumnPreference({
					tableId: tableName,
					columns: tableColumnModel,
				})
			);
			await this.savePreference();
			return;
		}

		existing.columns = tableColumnModel;
		await this.savePreference();
		return;
	}

	async savePreference() {
		const resp = await this.focuslink.userPreferenceSave(this.userSubscription.value.preferences);
		console.log(`User preferences updated`);
	}
}
